import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';

const Contact: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const form = event.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/xjkalwon', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setShowSuccess(true);
        form.reset();
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // You could add error handling here if needed
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#1976d2',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('contact.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('contact.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom>
                {t('contact.form.title')}
              </Typography>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="firstName"
                      label={t('contact.form.firstName')}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      name="lastName"
                      label={t('contact.form.lastName')}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="email"
                      label={t('contact.form.email')}
                      required
                      type="email"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="phone"
                      label={t('contact.form.phone')}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="message"
                      label={t('contact.form.message')}
                      required
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom>
                {t('contact.info.title')}
              </Typography>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('contact.info.address')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('contact.info.addressLine1')}
                    {/* <br />
                    {t('contact.info.addressLine2')} */}
                    <br />
                    {t('contact.info.addressLine3')}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {t('contact.info.phone')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('contact.info.phoneNumber')}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {t('contact.info.intlPhone')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('contact.info.intlPhoneNumber')}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {t('contact.info.email')}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {t('contact.info.emailAddress')}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {t('contact.info.hours')}
                  </Typography>
                  <Typography variant="body1">
                    {t('contact.info.weekdayHours')}
                    <br />
                    {t('contact.info.weekendHours')}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Success Message Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {t('contact.form.successMessage')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 